import React, { useMemo } from 'react';
import { applyConditionalLogic } from '../../utils/conditionalLogic';
import { SAFETY_QUESTIONS } from '../../data/safetyQuestions';

export default function SafetyQuestionsStep({ value, onChange, data }) {
    const handleQuestionChange = (field, checked) => {
        const newSafetyQuestions = { ...value.safetyQuestions, [field]: checked };
        let newRiskLevel = value.riskLevel;

        // Reset risk level if 'heet_werk' is unchecked
        if (field === 'heet_werk' && !checked) {
            newRiskLevel = 1;
        }

        onChange({
            ...value,
            safetyQuestions: newSafetyQuestions,
            riskLevel: newRiskLevel
        });
    };

    // Automatic Risk Triggering based on Task Name & Metadata
    React.useEffect(() => {
        if (!value.task) return;

        const taskLower = value.task.toLowerCase();
        const triggeredRisks = {};

        // 1. Load from Task Metadata (Pre-selection from Editor)
        if (data && data.task_metadata && data.task_metadata[value.task]) {
            const meta = data.task_metadata[value.task];

            // New format: safetyQuestions object
            if (meta.safetyQuestions) {
                Object.entries(meta.safetyQuestions).forEach(([key, val]) => {
                    if (val === true || val === 'Ja') {
                        triggeredRisks[key] = true;
                    }
                });
            }
            // Legacy format: Risico's object
            else if (meta["Risico's"]) {
                const legacyRisks = typeof meta["Risico's"] === 'object' ? meta["Risico's"] : {};
                if (legacyRisks['Elektrisch'] === 'Ja') triggeredRisks['elektrische_spanning'] = true;
                if (legacyRisks['Restdruk'] === 'Ja') triggeredRisks['restdruk'] = true;
                if (legacyRisks['Onverwachtse beweging'] === 'Ja') triggeredRisks['onverwachte_beweging'] = true;
                if (legacyRisks['Hoge temperatuur'] === 'Ja') triggeredRisks['gevaarlijke_temperaturen'] = true;
                if (legacyRisks['Gevaarlijke stoffen'] === 'Ja') triggeredRisks['contact_processtoffen'] = true;
            }
        }

        // 2. Fallback: Keyword Matching (Smart Defaults)
        const hasMetadata = data && data.task_metadata && data.task_metadata[value.task] && (data.task_metadata[value.task].safetyQuestions || data.task_metadata[value.task]["Risico's"]);

        if (!hasMetadata) {
            const mappings = [
                { keywords: ['lassen', 'slijpen', 'branden', 'heet werk', 'laswerk', 'las'], risk: 'heet_werk' },
                {
                    keywords: ['motor', 'pomp', 'ventilator', 'aandrijving', 'elektro'],
                    risk: 'elektrische_spanning',
                    excludes: ['niet elektrisch', 'geen elektrisch', 'mechanisch']
                },
                { keywords: ['motor', 'pomp', 'draaiende', 'mechanisch'], risk: 'onverwachte_beweging' },
                { keywords: ['hoogte', 'dak', 'ladder', 'steiger', 'bordes'], risk: 'werken_op_hoogte' },
                { keywords: ['tank', 'silo', 'put', 'besloten'], risk: 'besloten_ruimte' },
                { keywords: ['chemisch', 'zuur', 'loog', 'reinigen', 'gevaarlijke stof'], risk: 'gevaarlijke_chemicalien' },
                { keywords: ['hijsen', 'takelen', 'kraan'], risk: 'gehesen' },
                { keywords: ['druk', 'pneumatiek', 'hydrauliek', 'leiding'], risk: 'restdruk' },
                { keywords: ['schoonmaken', 'reinigen'], risk: 'schoonmaken_na_werk' },
                { keywords: ['glas', 'hard plastic'], risk: 'glas_hard_plastic' }
            ];

            mappings.forEach(mapping => {
                const hasKeyword = mapping.keywords.some(kw => taskLower.includes(kw));
                const hasExclude = mapping.excludes && mapping.excludes.some(ex => taskLower.includes(ex));

                if (hasKeyword && !hasExclude) {
                    triggeredRisks[mapping.risk] = true;
                }
            });
        }

        // Check if we need to update to avoid infinite loops
        const currentQuestions = value.safetyQuestions || {};
        const risksToAdd = {};
        let shouldUpdate = false;

        Object.keys(triggeredRisks).forEach(key => {
            // ONLY add if strictly undefined (never touched). 
            // If it is false (user unchecked), DO NOT overwrite.
            if (currentQuestions[key] === undefined) {
                risksToAdd[key] = true;
                shouldUpdate = true;
            }
        });

        if (shouldUpdate || (hasMetadata && Object.keys(triggeredRisks).length > 0 && !Object.keys(triggeredRisks).every(k => currentQuestions[k] !== undefined))) {
            // Second part of OR condition above is a safety check: if we have metadata risks, ensure they are accounted for.
            // But the main loop handles it. We can simplify.
            if (shouldUpdate) {
                onChange({
                    ...value,
                    safetyQuestions: { ...currentQuestions, ...risksToAdd }
                });
            }
        }
    }, [value.task, data]);

    const questions = SAFETY_QUESTIONS;

    const safetyQuestions = value.safetyQuestions || {};

    const previewData = useMemo(() => {
        return applyConditionalLogic(safetyQuestions, value);
    }, [safetyQuestions, value]);

    return (
        <div className="fade-in space-y-6">
            <h2 className="text-xl font-bold mb-4 text-white border-b border-slate-700 pb-2">Stap 4: Veiligheidsvragen</h2>
            <p className="text-sm text-slate-400 mb-6">
                Beantwoord de volgende vragen om automatisch de juiste veiligheidsmaatregelen, PBM's en vergunningen toe te voegen.
            </p>

            {/* Energy Risks Section MOVED TO STEP 2 */}
            {/* The logic is now handled in HierarchyStep, so we don't render it here to avoid duplication */}

            {/* Environment Risks Section */}
            <div className="card glass-panel border-slate-700/50">
                <h3 className="font-bold text-lg mb-4 text-orange-400 flex items-center gap-2">
                    🌍 Omgevingsrisico's
                </h3>
                <div className="flex flex-col space-y-2">
                    {questions.environment.map(q => (
                        <label key={q.id} className="flex items-start gap-3 cursor-pointer hover:bg-slate-700/50 p-3 rounded transition border border-transparent hover:border-slate-600">
                            <input
                                type="checkbox"
                                checked={safetyQuestions[q.id] || false}
                                onChange={(e) => handleQuestionChange(q.id, e.target.checked)}
                                className="w-5 h-5 mt-0.5 rounded border-slate-600 bg-slate-800 text-blue-500 focus:ring-2 focus:ring-blue-500 flex-shrink-0"
                            />
                            <span className="text-sm leading-relaxed text-slate-300">{q.label}</span>
                        </label>
                    ))}
                </div>
            </div>

            {/* Task-Specific Hazards Section */}
            <div className="card glass-panel border-slate-700/50">
                <h3 className="font-bold text-lg mb-4 text-red-400 flex items-center gap-2">
                    ⚠️ Taakspecifieke Gevaren
                </h3>
                <div className="flex flex-col space-y-2">
                    {questions.taskHazards.map(q => (
                        <div key={q.id} className="flex flex-col">
                            <label className="flex items-start gap-3 cursor-pointer hover:bg-slate-700/50 p-3 rounded transition border border-transparent hover:border-slate-600">
                                <input
                                    type="checkbox"
                                    checked={safetyQuestions[q.id] || false}
                                    onChange={(e) => handleQuestionChange(q.id, e.target.checked)}
                                    className="w-5 h-5 mt-0.5 rounded border-slate-600 bg-slate-800 text-blue-500 focus:ring-2 focus:ring-blue-500 flex-shrink-0"
                                />
                                <span className="text-sm leading-relaxed text-slate-300">{q.label}</span>
                            </label>

                            {/* Sub-questions Render Logic */}
                            {q.subQuestions && safetyQuestions[q.id] && (
                                <div className="ml-11 mt-1 space-y-2 border-l-2 border-slate-600 pl-4 py-2 animate-in slide-in-from-top-2 duration-200">
                                    {q.subQuestions.map(subQ => (
                                        <label key={subQ.id} className="flex items-start gap-3 cursor-pointer hover:bg-slate-700/30 p-2 rounded transition">
                                            <input
                                                type="checkbox"
                                                checked={safetyQuestions[subQ.id] || false}
                                                onChange={(e) => handleQuestionChange(subQ.id, e.target.checked)}
                                                className="w-4 h-4 mt-0.5 rounded border-slate-500 bg-slate-800 text-purple-500 focus:ring-purple-500 flex-shrink-0"
                                            />
                                            <span className="text-sm text-slate-400">{subQ.label}</span>
                                        </label>
                                    ))}
                                </div>
                            )}

                            {/* Risk Level Selector for Hot Work */}
                            {q.id === 'heet_werk' && safetyQuestions[q.id] && (
                                <div className="ml-11 mt-2 p-4 bg-slate-800/50 rounded-lg border border-slate-700 animate-in slide-in-from-top-2">
                                    <label className="label text-blue-400 mb-3 block">Classificatie van de taak (Risiconiveau)</label>
                                    <div className="grid grid-cols-1 gap-3">
                                        {[
                                            { level: 1, label: 'Niveau 1: Niet-kritisch', desc: 'Geen specifieke certificaten vereist.' },
                                            { level: 2, label: 'Niveau 2: Constructief / Druk', desc: 'Vereist: PED of NEN-EN 1090.' },
                                            { level: 3, label: 'Niveau 3: Hygiënisch', desc: 'Vereist: Hygiëne certificaat.' }
                                        ].map((opt) => (
                                            <label
                                                key={opt.level}
                                                className={`
                                                    relative flex items-start gap-3 p-3 rounded border-2 cursor-pointer transition-all
                                                    ${value.riskLevel === opt.level
                                                        ? 'border-blue-500 bg-blue-500/10'
                                                        : 'border-slate-700 hover:border-slate-600 bg-slate-900/50'}
                                                `}
                                            >
                                                <input
                                                    type="radio"
                                                    name="riskLevel"
                                                    className="mt-1 w-4 h-4 text-blue-500 bg-slate-800 border-slate-600 focus:ring-blue-500 focus:ring-2"
                                                    checked={value.riskLevel === opt.level}
                                                    onChange={() => onChange({ ...value, riskLevel: opt.level })}
                                                />
                                                <div className="flex flex-col">
                                                    <span className={`font-bold ${value.riskLevel === opt.level ? 'text-blue-400' : 'text-slate-300'}`}>
                                                        {opt.label}
                                                    </span>
                                                    <span className="text-xs text-slate-500 mt-0.5">
                                                        {opt.desc}
                                                    </span>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Hygiene Section */}
            <div className="card glass-panel border-slate-700/50">
                <h3 className="font-bold text-lg mb-4 text-blue-400 flex items-center gap-2">
                    🧹 Hygiëne & Voedselveiligheid
                </h3>
                <div className="flex flex-col space-y-2">
                    {questions.hygiene.map(q => (
                        <label key={q.id} className="flex items-start gap-3 cursor-pointer hover:bg-slate-700/50 p-3 rounded transition border border-transparent hover:border-slate-600">
                            <input
                                type="checkbox"
                                checked={safetyQuestions[q.id] || false}
                                onChange={(e) => handleQuestionChange(q.id, e.target.checked)}
                                className="w-5 h-5 mt-0.5 rounded border-slate-600 bg-slate-800 text-blue-500 focus:ring-2 focus:ring-blue-500 flex-shrink-0"
                            />
                            <span className="text-sm leading-relaxed text-slate-300">{q.label}</span>
                        </label>
                    ))}
                </div>
            </div>

            {/* Live Preview Panel */}
            {Object.values(safetyQuestions).some(v => v) && (
                <div className="sticky bottom-4 z-10 card bg-slate-900/95 border-t-4 border-blue-500 shadow-2xl backdrop-blur-sm p-4 animate-slide-up">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="font-bold text-white flex items-center gap-2">
                            ✨ Gevolgen van uw keuzes
                        </h3>
                        <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded-full">Live Preview</span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 text-sm">
                        {previewData.lotoRequired && (
                            <div className="bg-yellow-900/30 p-2 rounded border border-yellow-700/50">
                                <div className="font-bold text-yellow-400 flex items-center gap-1">
                                    ⚠️ LOTO Vereist
                                </div>
                                <div className="text-xs text-yellow-200/70 mt-1">
                                    {previewData.lotoSteps.length} procedure(s) toegevoegd
                                </div>
                            </div>
                        )}

                        {previewData.permits.length > 0 && (
                            <div className="bg-red-900/30 p-2 rounded border border-red-700/50">
                                <div className="font-bold text-red-400 flex items-center gap-1">
                                    📝 Vergunningen
                                </div>
                                <div className="text-xs text-red-200/70 mt-1">
                                    {previewData.permits.join(', ')}
                                </div>
                            </div>
                        )}

                        {previewData.additionalPbms.length > 0 && (
                            <div className="bg-blue-900/30 p-2 rounded border border-blue-700/50">
                                <div className="font-bold text-blue-400 flex items-center gap-1">
                                    🛡️ Extra PBM's
                                </div>
                                <div className="text-xs text-blue-200/70 mt-1">
                                    {previewData.additionalPbms.length} item(s) toegevoegd
                                </div>
                            </div>
                        )}

                        {previewData.additionalRisks.length > 0 && (
                            <div className="bg-slate-700/30 p-2 rounded border border-slate-600/50">
                                <div className="font-bold text-slate-300 flex items-center gap-1">
                                    ⚠️ Extra Risico's
                                </div>
                                <div className="text-xs text-slate-400 mt-1">
                                    {previewData.additionalRisks.length} risico('s) toegevoegd
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
